import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ApiError } from "../../api-errors.ts";
import { navigate, REPORT_TABS, type ReportTab, reportsPath, useSearch } from "../../router.tsx";
import { useAdminApi } from "../admin-api.tsx";
import { type ReportFilters, useAuditAccess, useReport, useScopeEntities } from "./queries.ts";
import { RecommendationsSection } from "./Recommendations.tsx";
import { CostTab, HealthTab, QualityTab, UsageTab } from "./tabs.tsx";

const DAY_MS = 24 * 60 * 60 * 1000;
const today = () => new Date().toISOString().slice(0, 10);
const daysBefore = (day: string, n: number) =>
  new Date(new Date(`${day}T00:00:00Z`).getTime() - n * DAY_MS).toISOString().slice(0, 10);

type Preset = "last7" | "last30" | "last90" | "last13Months" | "custom";
/** Days covered by each preset, ending today (13 months: the rollups' retention). */
const PRESET_DAYS: Record<Exclude<Preset, "custom">, number> = {
  last7: 7,
  last30: 30,
  last90: 90,
  last13Months: 396,
};

function presetOf(filters: ReportFilters): Preset {
  if (!filters.from && !filters.to) {
    return "last30";
  }
  const to = today();
  for (const [preset, days] of Object.entries(PRESET_DAYS)) {
    if (filters.to === to && filters.from === daysBefore(to, days - 1)) {
      return preset as Preset;
    }
  }
  return "custom";
}

/**
 * Backoffice reports (spec `usage-reporting`): four tabs over one period and entity, read from the
 * hourly rollup. The URL holds the tab and filters, so a view can be shared and reloaded.
 */
export function Reports() {
  const { t } = useTranslation();
  const { base } = useAdminApi();
  const search = useSearch();
  const requested = search.get("tab");
  const tab: ReportTab = REPORT_TABS.includes(requested as ReportTab)
    ? (requested as ReportTab)
    : "usage";
  const filters: ReportFilters = {
    from: search.get("from") ?? undefined,
    to: search.get("to") ?? undefined,
    entity: search.get("entity") ?? undefined,
  };
  const [entityReset, setEntityReset] = useState(false);
  const go = (next: Partial<ReportFilters> & { tab?: ReportTab }) =>
    navigate(reportsPath(next.tab ?? tab, { ...filters, ...next }, base), { replace: true });

  return (
    <div data-esi-content="people" className="flex flex-col gap-6">
      <PageHeader title={t("reports.title")} lead={t("reports.lead")} />
      <ReportFiltersBar
        filters={filters}
        onChange={(next) => {
          setEntityReset(false);
          go(next);
        }}
      />
      {entityReset ? <Notice tone="info">{t("reports.entityReset")}</Notice> : null}
      <Tabs value={tab} onValueChange={(next) => go({ tab: next as ReportTab })}>
        <TabsList
          variant="line"
          className="w-full justify-start overflow-x-auto border-b border-line"
        >
          {REPORT_TABS.map((name) => (
            <TabsTrigger key={name} value={name} className="flex-none">
              {t(`reports.tabs.${name}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={tab} className="pt-4">
          <ReportBody
            tab={tab}
            filters={filters}
            onEntityRefused={() => {
              setEntityReset(true);
              go({ entity: undefined });
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ReportBody(props: {
  tab: ReportTab;
  filters: ReportFilters;
  onEntityRefused: () => void;
}) {
  const report = useReport(props.tab, props.filters);
  const auditLinks = useAuditAccess();
  const refused =
    report.error instanceof ApiError && report.error.status === 404 && !!props.filters.entity;
  const { onEntityRefused } = props;
  useEffect(() => {
    if (refused) {
      onEntityRefused();
    }
  }, [refused, onEntityRefused]);

  if (report.isPending) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (report.isError) {
    return refused ? null : <Notice tone="danger">{report.error.message}</Notice>;
  }
  const data = report.data;
  switch (props.tab) {
    case "usage":
      return <UsageTab report={data as never} />;
    case "quality":
      return (
        <div className="flex flex-col gap-8">
          <QualityTab report={data as never} />
          <RecommendationsSection filters={props.filters} />
        </div>
      );
    case "cost":
      return <CostTab report={data as never} />;
    case "health":
      return <HealthTab report={data as never} auditLinks={auditLinks} />;
  }
}

function ReportFiltersBar(props: {
  filters: ReportFilters;
  onChange: (next: Partial<ReportFilters>) => void;
}) {
  const { t } = useTranslation();
  const ids = { period: useId(), entity: useId(), from: useId(), to: useId() };
  const entities = useScopeEntities();
  const preset = presetOf(props.filters);
  const [custom, setCustom] = useState(preset === "custom");
  const [from, setFrom] = useState(props.filters.from ?? daysBefore(today(), 29));
  const [to, setTo] = useState(props.filters.to ?? today());
  const choose = (value: Preset) => {
    if (value === "custom") {
      setCustom(true);
      return;
    }
    setCustom(false);
    const end = today();
    props.onChange({ from: daysBefore(end, PRESET_DAYS[value] - 1), to: end });
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex w-48 flex-col gap-1.5">
        <Label htmlFor={ids.period}>{t("reports.filters.period")}</Label>
        <NativeSelect
          id={ids.period}
          value={custom ? "custom" : preset}
          onChange={(e) => choose(e.target.value as Preset)}
        >
          {(["last7", "last30", "last90", "last13Months", "custom"] as const).map((p) => (
            <option key={p} value={p}>
              {t(`reports.filters.${p}`)}
            </option>
          ))}
        </NativeSelect>
      </div>
      {custom ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            props.onChange({ from, to });
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.from}>{t("reports.filters.from")}</Label>
            <Input
              id={ids.from}
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.to}>{t("reports.filters.to")}</Label>
            <Input
              id={ids.to}
              type="date"
              value={to}
              min={from}
              max={today()}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <Button type="submit" variant="outline">
            {t("reports.filters.apply")}
          </Button>
        </form>
      ) : null}
      {entities.data && entities.data.length > 1 ? (
        <div className="flex w-56 flex-col gap-1.5">
          <Label htmlFor={ids.entity}>{t("reports.filters.entity")}</Label>
          <NativeSelect
            id={ids.entity}
            value={props.filters.entity ?? ""}
            onChange={(e) => props.onChange({ entity: e.target.value || undefined })}
          >
            <option value="">{t("reports.filters.allEntities")}</option>
            {entities.data.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </NativeSelect>
        </div>
      ) : null}
    </div>
  );
}
